import React, {
	type Dispatch,
	Fragment,
	type MutableRefObject,
	type ReactNode,
	type RefObject,
	type SetStateAction,
	useRef,
	useState,
} from 'react';

import { styled } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import LoadingButton from '@atlaskit/button/loading-button';
import Image from '@atlaskit/image';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

import { useProductDependencies } from '../../../../../controllers/use-product-dependencies';
import { noResults as noResultsImage } from '../../../../assets';
import {
	DEFAULT_UNSPLASH_QUERY_WORD,
	PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD,
} from '../../constants';
import type { SelectedImage, UnsplashResult } from '../../types';
import { getUnsplashPhotos } from '../../utils/unsplash-api';

import { messages } from './messages';

type UnsplashSearchContentProps = {
	analyticsSource: string;
	dropdownPopupComponentRef?: RefObject<HTMLElement | null>;
	handleError: (error: Error) => void;
	handleImageSelection: (props: SelectedImage) => void;
	id?: string;
	idPrefix: string;
	isPageTitleComponent?: boolean;
	loadMoreText: Record<string, ReactNode>;
	pageNumber: MutableRefObject<number>;
	queryWord: MutableRefObject<string>;
	searchResultsRef: RefObject<HTMLDivElement>;
	setUnsplashResultsCountFromLastAPICall: Dispatch<SetStateAction<number>>;
	setUnsplashSearchResults: Dispatch<SetStateAction<UnsplashResult[] | undefined>>;
	unsplashResultsCountFromLastAPICall: number;
	unSplashSearchResults: UnsplashResult[] | undefined;
};

const UNSPLASH_LICENSE_LINK = 'https://unsplash.com/license';

const UNSPLASH_TERMS_OF_SERVICE_LINK = 'https://unsplash.com/terms';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchContentContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	maxWidth: '264.14px !important',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchResultsContainer = styled.div({
	display: 'grid',
	maxHeight: '390px',
	overflow: 'scroll',
	gridTemplateColumns: 'auto auto',
	gap: token('space.100'),
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashWrapper = styled.li({
	display: 'inline-grid',
	position: 'relative',
	width: '120.07px',
	margin: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageResultsList = styled.ul({
	display: 'contents',
	listStyle: 'none',
	margin: 0,
	padding: 0,
});

const artistInfoSelector = 'items.ArtistInfo';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArtistInfo = styled.div<{ 'data-styled-selector'?: string }>({
	backgroundColor: token('color.background.inverse.subtle.hovered'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderBottomLeftRadius: token('radius.small', '3px'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderBottomRightRadius: token('radius.small', '3px'),
	position: 'absolute',
	bottom: 0,
	width: '100%',
	font: token('font.body.small'),
	opacity: 1,
	visibility: 'hidden',
	'&:hover': {
		visibility: 'visible',
	},
});

// eslint-disable-next-line @atlaskit/design-system/no-html-anchor, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArtistLink = styled.a({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	textDecoration: 'none !important',
});

ArtistInfo.defaultProps = {
	'data-styled-selector': artistInfoSelector,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArtistNameWrapper = styled.div({
	marginLeft: token('space.050'),
	color: token('color.text.inverse'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const baseImageStyles = {
	width: '100%',
	height: '77.93px',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashImage = styled.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...baseImageStyles,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashImageWrapper = styled.button({
	border: 'none',
	background: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	[`&:hover + [data-styled-selector="${artistInfoSelector}"]`]: {
		visibility: 'visible',
	},
	cursor: 'pointer',
	padding: 0,
	height: '77.93px',
	outlineColor: token('color.border.information'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashLegalTerms = styled.div({
	paddingTop: token('space.100'),
	paddingRight: 0,
	paddingBottom: token('space.100'),
	paddingLeft: 0,
	backgroundColor: token('color.background.accent.gray.subtlest'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderBottomLeftRadius: token('radius.small', '3px'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderBottomRightRadius: token('radius.small', '3px'),
	color: token('color.text.subtle'),
	font: token('font.body.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/use-primitives
const LegalTextWrapper = styled.div({
	marginTop: token('space.0'),
	marginRight: token('space.100'),
	marginBottom: token('space.0'),
	marginLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnderlinedLink = styled.a({
	textDecoration: 'underline',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	color: `${token('color.text.subtlest')} !important`, // Using color.text.subtlest here because color.link is blue
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EndofResultsWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	gridColumnStart: 'span 2',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashSearchErrorWrapper = styled.div({
	display: 'flex',
	height: '164px',
	gridColumn: 'span 3',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchErrorText = styled.div({
	color: token('color.text.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImagePlaceholder = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...baseImageStyles,
	backgroundColor: token('color.background.neutral'),
});

const UnsplashLicenseLink = () => (
	<UnderlinedLink target="_blank" href={UNSPLASH_LICENSE_LINK}>
		<FormattedMessage {...messages.unsplashLicenseLink} />
	</UnderlinedLink>
);

const UnsplashTermsOfServiceLink = () => (
	<UnderlinedLink target="_blank" href={UNSPLASH_TERMS_OF_SERVICE_LINK}>
		<FormattedMessage {...messages.unsplashTermsOfServiceLink} />
	</UnderlinedLink>
);

export const UnsplashSearchContent: React.FC<UnsplashSearchContentProps> = ({
	unSplashSearchResults,
	id,
	idPrefix,
	handleImageSelection,
	loadMoreText,
	queryWord,
	pageNumber,
	setUnsplashSearchResults,
	handleError,
	searchResultsRef,
	unsplashResultsCountFromLastAPICall,
	setUnsplashResultsCountFromLastAPICall,
	dropdownPopupComponentRef,
	isPageTitleComponent,
	analyticsSource,
}) => {
	const [showButtonSpinner, setShowButtonSpinner] = useState<boolean>(false);
	const imageRef = useRef<HTMLButtonElement>(null);
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onImageClick = ({ fullSizeImageUrl, altDescription, downloadLink }: SelectedImage) => {
		handleImageSelection({ fullSizeImageUrl, altDescription, downloadLink });

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'selected',
				actionSubject: 'image',
				source: analyticsSource,
				attributes: {
					imageSource: 'unsplashSearch',
				},
			},
		}).fire();
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, result: UnsplashResult) => {
		if (e.key === 'Enter' || e.key === ' ') {
			onImageClick(result);
		}
	};

	const { submitError } = useProductDependencies();

	const onClickLoadMoreButton = () => {
		setShowButtonSpinner(true);
		pageNumber.current++;
		const defaultQueryWord = isPageTitleComponent
			? PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD
			: DEFAULT_UNSPLASH_QUERY_WORD;
		const queryKeyword = queryWord.current || defaultQueryWord;
		void getUnsplashPhotos(queryKeyword, pageNumber.current, submitError, isPageTitleComponent)
			.then((results) => {
				setUnsplashResultsCountFromLastAPICall(results.length);
				setUnsplashSearchResults(unSplashSearchResults!.concat(results));
				setShowButtonSpinner(false);
				imageRef.current?.focus();
			})
			.catch((error) => {
				handleError(error);
			});

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'loadMoreUnsplashImagesButtonClicked',
				source: analyticsSource,
			},
		}).fire();
	};

	const UnsplashLegalText = () => {
		return (
			<LegalTextWrapper>
				<FormattedMessage
					{...messages.unsplashLegal}
					values={{
						unsplashLicenseLink: <UnsplashLicenseLink />,
						unsplashTermsOfServiceLink: <UnsplashTermsOfServiceLink />,
					}}
				/>
			</LegalTextWrapper>
		);
	};

	const resultsCount = unSplashSearchResults?.filter(Boolean).length ?? 0;

	return (
		<SearchContentContainer
			id={id}
			ref={dropdownPopupComponentRef as RefObject<HTMLDivElement>}
			onClick={(e) => e.stopPropagation()}
		>
			{fg('a11y-fix-unsplash-combobox-aria') && (
				<span
					/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- live region must be visually hidden */
					style={{
						position: 'absolute',
						width: '1px',
						height: '1px',
						padding: 0,
						margin: '-1px',
						overflow: 'hidden',
						clip: 'rect(0,0,0,0)',
						whiteSpace: 'nowrap',
						border: 0,
					}}
					/* eslint-enable @atlaskit/ui-styling-standard/enforce-style-prop */
					role="status"
					aria-atomic="true"
					aria-live="polite"
				>
					{resultsCount > 0
						? formatMessage(messages.unsplashResultsCount, { count: resultsCount })
						: formatMessage(messages.unsplashNoResultsAnnouncement)}
				</span>
			)}
			<SearchResultsContainer
				ref={searchResultsRef}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- listbox role requires id matching aria-controls on input
				{...(fg('a11y-fix-unsplash-combobox-aria')
					? {
							role: 'listbox' as const,
							'aria-label': formatMessage(messages.unsplashSearchResultsLabel),
						}
					: {})}
			>
				{unSplashSearchResults?.length ? (
					<Fragment>
						<ImageResultsList>
							{unSplashSearchResults.map((result, index) =>
								result ? (
									<UnsplashWrapper
										key={index}
										data-testid={`${idPrefix}-unsplash-image-${result.id}`}
										onKeyDown={(e) => onKeyDown(e, result)}
									>
										<UnsplashImageWrapper
											// assign ref to image result only when it is the first of 30 new images loaded more.
											// This ref is necessary for placing focus on the image result away from load more button
											ref={index === unSplashSearchResults.length - 30 ? imageRef : null}
											onClick={() => onImageClick(result)}
											{...(fg('a11y-fix-unsplash-combobox-aria')
												? {
														role: 'option' as const,
														'aria-selected': false as const,
														id: `${idPrefix}-option-${index}`,
													}
												: {})}
										>
											<UnsplashImage src={result.smallSizeImageUrl} alt={result.altDescription} />
										</UnsplashImageWrapper>
										<ArtistInfo>
											<ArtistLink href={result.artistUrl} target="_blank">
												<ArtistNameWrapper>{result.artistName}</ArtistNameWrapper>
											</ArtistLink>
										</ArtistInfo>
									</UnsplashWrapper>
								) : (
									<UnsplashWrapper key={index}>
										<ImagePlaceholder data-testid="unsplash-popup-image-placeholder" />
									</UnsplashWrapper>
								),
							)}
						</ImageResultsList>
						<EndofResultsWrapper>
							{unsplashResultsCountFromLastAPICall === 30 ? (
								<LoadingButton
									onClick={onClickLoadMoreButton}
									spacing="compact"
									isLoading={showButtonSpinner}
									aria-label={formatMessage(messages.unsplashLoadMoreAriaLabel)}
								>
									<FormattedMessage {...loadMoreText} />
								</LoadingButton>
							) : (
								<SearchErrorText>
									<FormattedMessage {...messages.unsplashNoMoreResultsToLoadText} />
								</SearchErrorText>
							)}
						</EndofResultsWrapper>
					</Fragment>
				) : (
					<UnsplashSearchErrorWrapper>
						<Image src={noResultsImage} alt="" />
						<SearchErrorText>
							<FormattedMessage {...messages.unsplashNoResultsText} />
						</SearchErrorText>
					</UnsplashSearchErrorWrapper>
				)}
			</SearchResultsContainer>
			<UnsplashLegalTerms>
				<UnsplashLegalText />
			</UnsplashLegalTerms>
		</SearchContentContainer>
	);
};
